import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from '../core/config/app-config.service';
import {Observable} from 'rxjs';
import {WorkAbroad} from '../admin/models/workAbroad.model';
import {Employee} from '../admin/models/employee.model';
import {Vacation} from '../admin/models/vacation.model';
import {PersonalService} from '../personal/personal.service';
import {CountryModel} from '../admin/models/country.model';
import {EmployeeWorkAbroadAllowance} from '../admin/models/employeeWorkAbroadAllowance.model';
import {PublicHoliday} from '../admin/models/publicHoliday.model';
import {AbsenceService} from '../shared/globals/absence-service.service';
import {DatePipe} from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class WorkAbroadService {

    private readonly baseUrl;

    constructor(private personalService: PersonalService,
                private httpClient: HttpClient,
                private readonly configService: AppConfigService,
                private readonly absenceService: AbsenceService) {
        this.baseUrl = configService.getConfig().baseUrl;
    }

    /**
     * Takes in an array of country models and returns an array of the country models names
     * @param data A country model array
     */
    public getCountryNameArray(data: CountryModel[]): string[] {
        const array: string[] = [];
        data.forEach((country) => {
            array.push(country.name);
        });
        return array;
    }

    /**
     * Calculated the number of pending work abroad requests for the selected year
     * @param data The employee to check for pending requests
     * @param year The year to search for pending requests in
     */
    public pendingWorkAbroadRequests(data: Employee, year: number): number {
        let counter = 0;
        data.workAbroad.forEach((value) => {
            if (value.requestStatus === 'PENDING' &&
                Number(this.personalService.getYearForMatCard(value.endDate)) == year) {
                counter++;
            }
        });
        return counter;
    }

    /**
     * Calculated the number of pending work abroad days for the selected year
     * @param data The employee to check for pending requests
     * @param year The year to search for pending requests in
     */
    public pendingWorkAbroadDays(data: Employee, year: number): number {
        let counter = 0;
        data.workAbroad.forEach((value) => {
            if (value.requestStatus === 'PENDING' &&
                Number(this.personalService.getYearForMatCard(value.endDate)) == year) {
                counter = counter + value.numberOfWorkAbroadDays;
            }
        });
        return counter;
    }

    /**
     * Caculates the number of annual leave total taken in a work abroad request
     * @param workAbroad The work abroad request to search for annual leave in
     */
    public calculateAnnualLeaveTotal(workAbroad: WorkAbroad): number {
        let counter = 0;
        workAbroad.vacations.forEach((value) => {
            counter = counter + value.numberOfAnnualLeaveDays;
        });
        return counter;
    }

    /**
     * Submits a new work abroad request
     * @param data The work abroad request to be submitted
     */
    public submitWorkAbroadRequest(data: WorkAbroad): Observable<null> {
        return this.absenceService.postRequest(this.baseUrl, '/work-abroad', data);
    }

    /**
     * Updates a work abroad request
     * @param data The work abroad request to be updated
     */
    public modifyWorkAbroadRequest(data: WorkAbroad): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/work-abroad', data);
    }

    /**
     * Cancels a work abroad request
     * @param data The work abroad request to be cancelled
     */
    public cancelWorkAbroadRequest(data: string): Observable<null> {
        return this.absenceService.deleteRequest(this.baseUrl, '/work-abroad/cancel/' + data);
    }

    /**
     * Sorts the allowances by year of rollingStartPeriod, goes into the first year after the current date, and then
     * checks if the currentDate is before the nextTripAllowedFrom field, and returns whichever is first.
     * @param allowances The employeeWorkAbroadAllowance array to be tested
     * @param currentDate The currentDate
     */
    public getCurrentWorkAbroadAllowance(allowances: EmployeeWorkAbroadAllowance[], currentDate: Date): Date {
        let switcher = false;
        let correctDate = currentDate;
        allowances.sort((a, b) => (parseInt(this.personalService.getYearOfDateString(a.rollingPeriodStart)) - parseInt(this.personalService.getYearOfDateString(b.rollingPeriodStart))));
        allowances.forEach((allowance) => {
            if(currentDate.getTime() === new Date('01-02-2023').getTime()){
                correctDate = new Date('01-02-2023');
            }else if (currentDate > new Date(allowance.rollingPeriodStart) && !switcher) {
                switcher = true;
                if (new Date(allowance.nextTripAllowedFrom) > currentDate) {
                    correctDate = new Date(allowance.nextTripAllowedFrom);
                }
            }
        });
        return correctDate;
    }

    public setmaxWorkAbroadDate(allowances: EmployeeWorkAbroadAllowance[], minWorkAbroadDate: Date): string {
        let minWorkDate = new Date(minWorkAbroadDate).getTime();
        let maxWorkAbroadDate = '';
        allowances.sort((a, b) => (parseInt(this.personalService.getYearOfDateString(a.rollingPeriodEnd)) - parseInt(this.personalService.getYearOfDateString(b.rollingPeriodEnd))));
        allowances.forEach((allowance) => {
            let rollingEnd = new Date(allowance.rollingPeriodEnd).getTime();
            if ((Math.floor(rollingEnd - minWorkDate) / 1000 / 60 / 60 / 24) > 0) {
                maxWorkAbroadDate = allowance.rollingPeriodEnd;
            }
        });

        return maxWorkAbroadDate
    }

    /**
     * Calculates the last work working before a work abroad request, taking weekends and public holidays into account
     * @param date the date to be converted from startDate to lastDayWorking
     * @param publicHolidays the list of public holidays
     * @param vacations the Vacation data to be processed.
     */
    public getLastDayWorking(date: Date, publicHolidays: PublicHoliday[], vacations: Vacation[]): string {
        let tempDate = this.personalService.convertDateFormatForAPI(date.toDateString());
        date.setDate(date.getDate() - 1);
        while (tempDate !== this.personalService.convertDateFormatForAPI(date.toDateString())) {
            tempDate = this.personalService.convertDateFormatForAPI(date.toDateString());
            if (date.getDay() === 6 || date.getDay() === 0) {
                date.setDate(date.getDate() - 1);
            }
            let dateAsString = this.personalService.convertDateFormatForAPI(date.toDateString());
            publicHolidays.forEach((holiday) => {
                if (dateAsString === holiday.holidayDate) {
                    date.setDate(date.getDate() - 1);
                    dateAsString = this.personalService.convertDateFormatForAPI(date.toDateString());
                }
            });
            vacations.forEach((vacation) => {
                if (dateAsString === vacation.endDate) {
                    date = new Date(this.personalService.swapDateAndMonth(vacation.startDate));
                    date.setDate(date.getDate() - 1);
                    dateAsString = this.personalService.convertDateFormatForAPI(date.toDateString());
                }
            });
        }
        return this.personalService.convertDateFormatForAPI(date.toDateString());
    }

    /**
     * Calculates the first day back after a work abroad request, taking weekends and public holidays into account
     * @param date the date to be converted from endDate to firstDayBack
     * @param publicHolidays the list of public holidays
     * @param vacations the Vacation data to be processed.
     */
    public getFirstDayBack(date: Date, publicHolidays: PublicHoliday[], vacations: Vacation[]): string {
        let tempDate = this.personalService.convertDateFormatForAPI(date.toDateString());
        date.setDate(date.getDate() + 1);
        while (tempDate !== this.personalService.convertDateFormatForAPI(date.toDateString())) {
            tempDate = this.personalService.convertDateFormatForAPI(date.toDateString());
            if (date.getDay() === 6 || date.getDay() === 0) {
                date.setDate(date.getDate() + 1);
            }
            let dateAsString = this.personalService.convertDateFormatForAPI(date.toDateString());
            publicHolidays.forEach((holiday) => {
                if (dateAsString === holiday.holidayDate) {
                    date.setDate(date.getDate() + 1);
                    dateAsString = this.personalService.convertDateFormatForAPI(date.toDateString());
                }
            });
            vacations.forEach((vacation) => {
                if (dateAsString === vacation.startDate) {
                    date = new Date(this.personalService.swapDateAndMonth(vacation.endDate));
                    date.setDate(date.getDate() + 1);
                    dateAsString = this.personalService.convertDateFormatForAPI(date.toDateString());
                }
            });
        }
        return this.personalService.convertDateFormatForAPI(date.toDateString());
    }

    /**
     * Returns a string with yyyy/mm/dd format
     * @param dateString The datestring in format (dd/mm/yyyy) to converted
     */
    public convertDateToVacationFomat(dateString: string): string {
        return dateString.substring(6, 10) + '/' + dateString.substring(3, 5) + '/' + dateString.substring(0, 2);
    }

    /**
     * Returns a string with mm/dd/yyyy format
     * @param dateString The datestring in format (yyyy-mm-dd) to be converted
     */
    public convertVacationDateFormatToWorkAbroadFormat(dateString: string): any {
        return Number(dateString.substring(5, 7)) + '/' + Number(dateString.substring(8, 10)) + '/' + Number(dateString.substring(0, 4))
    }

    /**
     * Returns value which is used to check if the public holiday is selected
     * @param start the start of the date range
     * @param end the end of the date range
     */
    public checkPublicHoliday(start: string, end: string): any {
        return Math.floor(((Date.UTC(Number(end.substring(6, 10)), Number(end.substring(3, 5)), Number(end.substring(0, 2))) - Date.UTC(Number(start.substring(0, 4)), Number(start.substring(5, 7)), Number(start.substring(8, 10)))) / (1000 * 60 * 60 * 24)))
    }

    /**
     * Converts a date string to format dd/MM/yyyy. The api requires this formatting
     * @param dateString The string to convert
     */
    public convertDateString(dateString: string): string {
        const newDateString = new DatePipe('en-US').transform(dateString, 'dd/MM/yyyy');
        return newDateString ? newDateString : '';
    }

    /**
     * Sets the date to the 31st of December for next year based on the passed in date
     * @param date the date to be converted
     */
    public convertDateToNextYearEnd(date: Date): Date {
        date.setFullYear(date.getFullYear() + 2, 11, 31);
        return date;
    }
}
